import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Users, FileText, TrendingUp, AlertCircle, Monitor, MessageSquare, 
  Newspaper, Calendar, Eye, CheckCircle, Clock, XCircle, MapPin,
  Building, Mail, Phone, Globe, ChevronRight, X, Shield, UserPlus
} from 'lucide-react';
import api from '../../utils/api';
import Header from '../../components/Header';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ 
    pending: 0, 
    active: 0, 
    rfqs: 0, 
    openRfqs: 0,
    quotes: 0,
    pendingSoftware: 0,
    messages: 0 
  });
  
  // Modal states
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showRfqsModal, setShowRfqsModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  
  // Data for modals
  const [activeMembers, setActiveMembers] = useState([]);
  const [allRfqs, setAllRfqs] = useState([]);
  const [pendingApplications, setPendingApplications] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats({
        pending: response.data.companies?.pending || 0,
        active: response.data.companies?.active || 0,
        rfqs: response.data.rfqs?.total || 0,
        openRfqs: response.data.rfqs?.open || 0,
        quotes: response.data.quotes?.total || 0,
        pendingSoftware: response.data.software?.pending || 0,
        messages: response.data.messages?.total || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchActiveMembers = async () => {
    setLoadingData(true);
    try {
      const response = await api.get('/admin/companies');
      const active = response.data.filter(c => c.status === 'active');
      setActiveMembers(active);
      setShowMembersModal(true);
    } catch (error) {
      console.error('Failed to fetch members');
    } finally {
      setLoadingData(false);
    }
  };

  const fetchAllRfqs = async () => {
    setLoadingData(true);
    try {
      const response = await api.get('/admin/rfqs');
      setAllRfqs(response.data);
      setShowRfqsModal(true);
    } catch (error) {
      console.error('Failed to fetch RFQs');
    } finally {
      setLoadingData(false);
    }
  };

  const fetchPendingApplications = async () => {
    setLoadingData(true);
    try {
      const response = await api.get('/admin/applications');
      setPendingApplications(response.data);
      setShowPendingModal(true);
    } catch (error) {
      console.error('Failed to fetch applications');
    } finally {
      setLoadingData(false);
    }
  };

  const getRfqStatusBadge = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-700',
      closed: 'bg-slate-100 text-slate-700',
      awarded: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-outfit font-bold text-ocean-900">Admin Dashboard</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1 sm:mt-2">CTC Cargo Network Management</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
            {/* Pending Applications Card */}
            <Card 
              className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-orange-300" 
              onClick={fetchPendingApplications}
              data-testid="pending-applications-card"
            >
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600 mb-0.5 sm:mb-1">Pending Apps</p>
                    <p className="text-2xl sm:text-3xl font-bold text-orange-500">{stats.pending}</p>
                    <p className="text-[10px] sm:text-xs text-orange-400 mt-0.5 sm:mt-1">Click to view →</p>
                  </div>
                  <AlertCircle className="h-7 w-7 sm:h-10 sm:w-10 text-orange-200" />
                </div>
              </CardContent>
            </Card>

            {/* Active Members Card */}
            <Card 
              className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-green-300" 
              onClick={fetchActiveMembers}
              data-testid="active-members-card"
            >
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600 mb-0.5 sm:mb-1">Active Members</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-500">{stats.active}</p>
                    <p className="text-[10px] sm:text-xs text-green-400 mt-0.5 sm:mt-1">Click to view →</p>
                  </div>
                  <Users className="h-7 w-7 sm:h-10 sm:w-10 text-green-200" />
                </div>
              </CardContent>
            </Card>

            {/* Total RFQs Card */}
            <Card 
              className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-cyan-300" 
              onClick={fetchAllRfqs}
              data-testid="total-rfqs-card"
            >
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600 mb-0.5 sm:mb-1">Total RFQs</p>
                    <p className="text-2xl sm:text-3xl font-bold text-cyan-500">{stats.rfqs}</p>
                    <p className="text-[10px] sm:text-xs text-cyan-400 mt-0.5 sm:mt-1">{stats.openRfqs} open →</p>
                  </div>
                  <FileText className="h-7 w-7 sm:h-10 sm:w-10 text-cyan-200" />
                </div>
              </CardContent>
            </Card>

            {/* Pending Software Card */}
            <Card 
              className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-purple-300" 
              onClick={() => navigate('/admin/cms')}
            >
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600 mb-0.5 sm:mb-1">Pending SW</p>
                    <p className="text-2xl sm:text-3xl font-bold text-purple-500">{stats.pendingSoftware}</p>
                    <p className="text-[10px] sm:text-xs text-purple-400 mt-0.5 sm:mt-1">Manage →</p>
                  </div>
                  <Monitor className="h-7 w-7 sm:h-10 sm:w-10 text-purple-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Active Members Modal */}
          <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
            <DialogContent className="w-[95vw] max-w-4xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base sm:text-xl">
                  <Users className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
                  Active Members ({activeMembers.length})
                </DialogTitle>
              </DialogHeader>
              <div className="mt-3 sm:mt-4">
                {loadingData ? (
                  <div className="flex justify-center py-6 sm:py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                  </div>
                ) : activeMembers.length === 0 ? (
                  <p className="text-center py-6 sm:py-8 text-slate-500 text-sm">No active members found</p>
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {activeMembers.map((company) => (
                      <div 
                        key={company.id} 
                        className="border rounded-lg p-3 sm:p-4 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                              <h3 className="font-semibold text-sm sm:text-base text-ocean-900 truncate">{company.legal_name}</h3>
                              <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                              {company.trust_score && (
                                <Badge variant="outline" className="text-xs">Trust: {company.trust_score}%</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-slate-600 mt-1 sm:mt-2">
                              {company.country && (
                                <span className="flex items-center gap-1">
                                  <Globe className="h-3 w-3" />
                                  {company.country}
                                </span>
                              )}
                              {company.contact_email && (
                                <span className="flex items-center gap-1 truncate max-w-[150px] sm:max-w-none">
                                  <Mail className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">{company.contact_email}</span>
                                </span>
                              )}
                            </div>
                            {company.services && company.services.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {company.services.slice(0, 3).map((service, i) => (
                                  <Badge key={i} variant="secondary" className="text-[10px] sm:text-xs capitalize">
                                    {service.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                                {company.services.length > 3 && (
                                  <Badge variant="secondary" className="text-[10px] sm:text-xs">
                                    +{company.services.length - 3}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full sm:w-auto text-xs sm:text-sm"
                            onClick={() => {
                              setShowMembersModal(false);
                              navigate(`/company/${company.id}`);
                            }}
                          >
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row justify-between gap-2">
                  <Button variant="outline" onClick={() => setShowMembersModal(false)} className="w-full sm:w-auto">
                    Close
                  </Button>
                  <Button onClick={() => { setShowMembersModal(false); navigate('/admin/companies'); }} className="w-full sm:w-auto">
                    Manage All Companies
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* RFQs Modal */}
          <Dialog open={showRfqsModal} onOpenChange={setShowRfqsModal}>
            <DialogContent className="w-[95vw] max-w-4xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base sm:text-xl">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-500" />
                  All RFQs ({allRfqs.length})
                </DialogTitle>
              </DialogHeader>
              <div className="mt-3 sm:mt-4">
                {loadingData ? (
                  <div className="flex justify-center py-6 sm:py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                  </div>
                ) : allRfqs.length === 0 ? (
                  <p className="text-center py-6 sm:py-8 text-slate-500 text-sm">No RFQs found</p>
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {allRfqs.map((rfq) => (
                      <div 
                        key={rfq.id} 
                        className="border rounded-lg p-3 sm:p-4 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                              <h3 className="font-semibold text-sm sm:text-base text-ocean-900 truncate">{rfq.title || 'Untitled RFQ'}</h3>
                              {getRfqStatusBadge(rfq.status)}
                              {rfq.is_public && (
                                <Badge variant="outline" className="text-[10px] sm:text-xs">Public</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-600 mt-1">
                              <span className="flex items-center gap-1 font-medium">
                                <MapPin className="h-3 w-3" />
                                {rfq.origin} → {rfq.destination}
                              </span>
                              <span>{rfq.cargo_type}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-slate-400 mt-2">
                              <span className="truncate max-w-[120px] sm:max-w-none">Company: {rfq.company_name}</span>
                              <span>Created: {new Date(rfq.created_at).toLocaleDateString()}</span>
                              <span>{rfq.quotes_count || 0} quotes</span>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full sm:w-auto text-xs sm:text-sm"
                            onClick={() => {
                              setShowRfqsModal(false);
                              navigate(`/rfqs/${rfq.id}`);
                            }}
                          >
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row justify-between gap-2">
                  <Button variant="outline" onClick={() => setShowRfqsModal(false)} className="w-full sm:w-auto">
                    Close
                  </Button>
                  <Button onClick={() => { setShowRfqsModal(false); navigate('/admin/cms'); }} className="w-full sm:w-auto">
                    Manage RFQs in CMS
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Pending Applications Modal */}
          <Dialog open={showPendingModal} onOpenChange={setShowPendingModal}>
            <DialogContent className="w-[95vw] max-w-4xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base sm:text-xl">
                  <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
                  Pending Applications ({pendingApplications.length})
                </DialogTitle>
              </DialogHeader>
              <div className="mt-3 sm:mt-4">
                {loadingData ? (
                  <div className="flex justify-center py-6 sm:py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                  </div>
                ) : pendingApplications.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <CheckCircle className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-green-300 mb-2" />
                    <p className="text-slate-500 text-sm sm:text-base">No pending applications!</p>
                    <p className="text-xs sm:text-sm text-slate-400">All applications have been reviewed.</p>
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {pendingApplications.map((app) => (
                      <div 
                        key={app.id} 
                        className="border border-orange-200 bg-orange-50/50 rounded-lg p-3 sm:p-4"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                              <h3 className="font-semibold text-sm sm:text-base text-ocean-900 truncate">{app.legal_name}</h3>
                              <Badge className="bg-orange-100 text-orange-700 text-xs">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-slate-600 mt-1 sm:mt-2">
                              {app.country && (
                                <span className="flex items-center gap-1">
                                  <Globe className="h-3 w-3" />
                                  {app.country}
                                </span>
                              )}
                              {app.contact_email && (
                                <span className="flex items-center gap-1 truncate max-w-[150px] sm:max-w-none">
                                  <Mail className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">{app.contact_email}</span>
                                </span>
                              )}
                            </div>
                            {app.services && app.services.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {app.services.slice(0, 3).map((service, i) => (
                                  <Badge key={i} variant="secondary" className="text-[10px] sm:text-xs capitalize">
                                    {service.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                                {app.services.length > 3 && (
                                  <Badge variant="secondary" className="text-[10px] sm:text-xs">
                                    +{app.services.length - 3}
                                  </Badge>
                                )}
                              </div>
                            )}
                            <p className="text-[10px] sm:text-xs text-slate-400 mt-2">
                              Applied: {new Date(app.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-xs sm:text-sm"
                            onClick={() => {
                              setShowPendingModal(false);
                              navigate('/admin/applications');
                            }}
                          >
                            Review
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row justify-between gap-2">
                  <Button variant="outline" onClick={() => setShowPendingModal(false)} className="w-full sm:w-auto">
                    Close
                  </Button>
                  <Button 
                    className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
                    onClick={() => { setShowPendingModal(false); navigate('/admin/applications'); }}
                  >
                    Review All Applications
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-2 sm:space-y-3">
                <Button
                  onClick={() => navigate('/admin/applications')}
                  className="w-full justify-start bg-cyan-500 hover:bg-cyan-600 text-sm sm:text-base h-10 sm:h-11"
                  data-testid="review-applications-btn"
                >
                  <AlertCircle className="mr-2 h-4 w-4" />
                  Review Applications ({stats.pending})
                </Button>
                <Button
                  onClick={() => navigate('/admin/users')}
                  className="w-full justify-start bg-orange-500 hover:bg-orange-600 text-sm sm:text-base h-10 sm:h-11"
                  data-testid="manage-admins-btn"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Manage Admin Users
                </Button>
                <Button
                  onClick={() => navigate('/admin/companies')}
                  variant="outline"
                  className="w-full justify-start text-sm sm:text-base h-10 sm:h-11"
                  data-testid="manage-companies-btn"
                >
                  <Users className="mr-2 h-4 w-4" />
                  Manage Companies
                </Button>
                <Button
                  onClick={() => navigate('/admin/cms')}
                  variant="outline"
                  className="w-full justify-start text-sm sm:text-base h-10 sm:h-11"
                  data-testid="manage-content-btn"
                >
                  <Newspaper className="mr-2 h-4 w-4" />
                  Manage Content (CMS)
                </Button>
                <Button
                  onClick={() => navigate('/directory')}
                  variant="outline"
                  className="w-full justify-start text-sm sm:text-base h-10 sm:h-11"
                >
                  <Users className="mr-2 h-4 w-4" />
                  View Member Directory
                </Button>
                <Button
                  onClick={() => navigate('/rfqs')}
                  variant="outline"
                  className="w-full justify-start text-sm sm:text-base h-10 sm:h-11"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  View RFQ Marketplace
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">System Status</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-slate-600">API Status</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-[10px] sm:text-xs rounded-full">Operational</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-slate-600">Database</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-[10px] sm:text-xs rounded-full">Connected</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-slate-600">Email Service</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-[10px] sm:text-xs rounded-full">Active</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-slate-600">Real-time Chat</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-[10px] sm:text-xs rounded-full">Active</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Platform Statistics</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
                  <div className="text-center p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <p className="text-xl sm:text-3xl font-bold text-ocean-900">{stats.quotes}</p>
                    <p className="text-xs sm:text-sm text-slate-600">Total Quotes</p>
                  </div>
                  <div className="text-center p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <p className="text-xl sm:text-3xl font-bold text-ocean-900">{stats.messages}</p>
                    <p className="text-xs sm:text-sm text-slate-600">Messages Sent</p>
                  </div>
                  <div className="text-center p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <p className="text-xl sm:text-3xl font-bold text-ocean-900">{stats.active + stats.pending}</p>
                    <p className="text-xs sm:text-sm text-slate-600">Total Companies</p>
                  </div>
                  <div className="text-center p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <p className="text-xl sm:text-3xl font-bold text-ocean-900">{stats.openRfqs}</p>
                    <p className="text-xs sm:text-sm text-slate-600">Active RFQs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
